import { useSnackbar } from 'notistack';
import { Button, CircularProgress, IconButton, Stack } from '@mui/material';
import { orange } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useState } from 'react';
import {
  COMMON_STRING,
  DELETE_CONFIRMATION_STRING,
  ERROR_STRING,
  SUCCESS_STRING,
} from '@/constants/dictionary';
import {
  AddFixtureActionMsg,
  HideSnackbarMsg,
  SCORING_WORKER_HOST_ACTION,
} from '@/workers/scoring/types';
import { FIXTURE_ACTION_TYPE } from '@/service/constants';
import { ScoringContext } from '@/contexts/scoring/context';
import {
  PlayersLeftVisualNotification,
  VISUAL_NOTIFICATIONS_PROPERTIES,
} from '@/contexts/visualNotifications/types';
import { ConfirmationButtonSet } from '../common/ConfirmationButtonSet';
import { MTDialog } from '../common/MTDialog';
import { SnackbarCloseActionProps } from './SnackbarCloseAction';

export interface SnackbarActionProps extends SnackbarCloseActionProps {
  playerLeft: PlayersLeftVisualNotification;
}

export const SnackbarPlayerLeft = ({
  playerLeft,
  snackbarId,
}: SnackbarActionProps) => {
  const {
    state: { fixtureId, collectionId },
    useDispatchWithResponse,
  } = useContext(ScoringContext);

  const [isOpenConfirmation, setIsOpenDeleteConfirmation] = useState(false);

  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const { dispatch: addAction, isLoading } =
    useDispatchWithResponse<AddFixtureActionMsg>(
      SCORING_WORKER_HOST_ACTION.ADD_FIXTURE_ACTION,
    );

  const { dispatch: hideSnackbar } = useDispatchWithResponse<HideSnackbarMsg>(
    SCORING_WORKER_HOST_ACTION.HIDE_SNACKBAR,
  );

  const submitAction = async () => {
    try {
      await addAction(
        {
          collectionId,
          fixtureId,
          teamId: playerLeft?.teamId,
          playerId: playerLeft?.playerId,
          fixtureActionTypeId: FIXTURE_ACTION_TYPE.PLAYER_RETURNED,
          withLastFixtureActionParams: true,
        },
        {
          successMessage: SUCCESS_STRING.ACTION_SUCCESSFULLY_ADDED,
        },
      );
    } catch {
      enqueueSnackbar(ERROR_STRING.FAILED_UPDATE_ACTION, { variant: 'error' });
    } finally {
      closeSnackbar(snackbarId);
    }
  };

  const onCloseSnackbar = async () => {
    try {
      await hideSnackbar({
        collectionId,
        fixtureId,
        type: VISUAL_NOTIFICATIONS_PROPERTIES.PLAYER_LEFT,
        actionId: playerLeft?.fixtureActionId || '',
      });
      setIsOpenDeleteConfirmation(false);
    } catch {
      enqueueSnackbar(ERROR_STRING.FAILED_UPDATE_ACTION, { variant: 'error' });
    } finally {
      closeSnackbar(snackbarId);
    }
  };

  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center' }} gap={1}>
      <Button
        disabled={isLoading || isOpenConfirmation}
        variant='text'
        startIcon={
          isLoading && <CircularProgress color={'inherit'} size={'1em'} />
        }
        onClick={submitAction}
        sx={{ color: orange[100] }}
      >
        {COMMON_STRING.PLAYER_RETURNED}
      </Button>
      <IconButton
        disabled={isOpenConfirmation}
        onClick={() => setIsOpenDeleteConfirmation(true)}
      >
        <CloseIcon />
      </IconButton>
      <MTDialog
        open={isOpenConfirmation}
        onClose={() => setIsOpenDeleteConfirmation(false)}
        title={COMMON_STRING.HIDE_SNACKBAR}
      >
        <ConfirmationButtonSet
          shouldTitle={false}
          confirmMessage={DELETE_CONFIRMATION_STRING.SNACKBAR_PLAYER_LEFT}
          onClose={() => setIsOpenDeleteConfirmation(false)}
          onConfirm={onCloseSnackbar}
        />
      </MTDialog>
    </Stack>
  );
};
